import { Search, Filter, Plus, Download } from "lucide-react"
import { Input } from "../ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"

export function PipelineFilters({ 
  searchTerm, 
  selectedSource, 
  selectedPriority, 
  selectedStatus, 
  selectedSettlementDate,
  onFilterChange, 
  onClearFilters,
  totalLeads,
  filteredLeads
}) {
  const sourceLabels = {
    realestate: "RealEstate.com.au",
    domain: "Domain.com.au",
    buyer_portal: "Buyer Portal",
    direct: "Direct Contact",
    voiceCaller: "Voice Caller",
    chatbot: "ChatBot",
    referral: "Referral",
    social: "Social Media",
    website: "Website",
    form: "Form",
    other: "Other Sources",
  };

  const priorityLabels = {
    high: "High Priority",
    medium: "Medium Priority",
    low: "Low Priority",
  };

  const stageLabels = {
    "New Lead": "New Lead",
    "Attempting Contact": "Attempting Contact",
    "Contact - Not Qualified": "Contact - Not Qualified",
    "Contacted Nurture": "Contacted Nurture",
    "Booked Appraisal": "Booked Appraisal",
    "Appraisal Complete - Awaiting Decision": "Appraisal Complete - Awaiting Decision",
    "Follow-Up Ongoing": "Follow-Up Ongoing",
    "Won - Listing Signed": "Won - Listing Signed",
    "Lost - Listed With Another Agent": "Lost - Listed With Another Agent",
    "Unresponsive - Cold": "Unresponsive - Cold",
  };

  const settlementDateOptions = [
    { value: "today", label: "Today" },
    { value: "next_30_days", label: "Next 30 Days" },
    { value: "next_60_days", label: "Next 60 Days" },
    { value: "next_90_days", label: "Next 90 Days" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "last_60_days", label: "Last 60 Days" },
    { value: "last_90_days", label: "Last 90 Days" },
  ];

  const hasActiveFilters = searchTerm || (selectedSource && selectedSource !== 'all') || (selectedPriority && selectedPriority !== 'all') || (selectedStatus && selectedStatus !== 'all') || (selectedSettlementDate && selectedSettlementDate !== 'all');

  return (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input 
              placeholder="Search leads..." 
              className="pl-10 w-64"
              value={searchTerm || ""}
              onChange={(e) => onFilterChange('search', e.target.value)}
            />
          </div>

          <Select value={selectedSource || "all"} onValueChange={(value) => onFilterChange('source', value === 'all' ? null : value)}>
            <SelectTrigger className="w-40 overflow-hidden">
              <SelectValue placeholder="Source" className="truncate text-left" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {Object.entries(sourceLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedPriority || "all"} onValueChange={(value) => onFilterChange('priority', value === 'all' ? null : value)}>
            <SelectTrigger className="w-40 overflow-hidden">
              <SelectValue placeholder="Priority" className="truncate text-left" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {Object.entries(priorityLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus || "all"} onValueChange={(value) => onFilterChange('status', value === 'all' ? null : value)}>
            <SelectTrigger className="w-40 overflow-hidden">
              <SelectValue placeholder="Stage" className="truncate text-left" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {Object.entries(stageLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedSettlementDate || "all"} onValueChange={(value) => onFilterChange('settlement_date', value === 'all' ? null : value)}>
            <SelectTrigger className="w-40 overflow-hidden">
              <SelectValue placeholder="Settlement Date" className="truncate text-left" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Settlement Dates</SelectItem>
              {settlementDateOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-600">
            Showing {filteredLeads} of {totalLeads} leads
          </span>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-600">Active filters:</span>
          
          {searchTerm && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <span>Search: {searchTerm}</span>
              <button 
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                onClick={() => onFilterChange('search', '')}
              >
                <span className="sr-only">Remove filter</span>×
              </button>
            </Badge>
          )}
          
          {selectedSource && selectedSource !== 'all' && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <span>Source: {sourceLabels[selectedSource]}</span>
              <button 
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                onClick={() => onFilterChange('source', 'all')}
              >
                <span className="sr-only">Remove filter</span>×
              </button>
            </Badge>
          )}
          
          {selectedPriority && selectedPriority !== 'all' && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <span>Priority: {priorityLabels[selectedPriority]}</span>
              <button 
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                onClick={() => onFilterChange('priority', 'all')}
              >
                <span className="sr-only">Remove filter</span>×
              </button>
            </Badge>
          )}
          
          {selectedStatus && selectedStatus !== 'all' && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <span>Stage: {stageLabels[selectedStatus]}</span>
              <button 
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                onClick={() => onFilterChange('status', 'all')}
              >
                <span className="sr-only">Remove filter</span>×
              </button>
            </Badge>
          )}

          {selectedSettlementDate && selectedSettlementDate !== 'all' && (
            <Badge variant="secondary" className="flex items-center space-x-1">
              <span>Settlement: {settlementDateOptions.find(opt => opt.value === selectedSettlementDate)?.label}</span>
              <button 
                className="ml-1 hover:bg-slate-300 rounded-full p-0.5"
                onClick={() => onFilterChange('settlement_date', 'all')}
              >
                <span className="sr-only">Remove filter</span>×
              </button>
            </Badge>
          )}
          
          <Button variant="ghost" size="sm" className="text-slate-500" onClick={onClearFilters}>
            Clear all
          </Button>
        </div>
      )}
    </div>
  )
}
